const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : 'http://localhost:7777/api/v1';

export interface SessionQuestion {
  id: string;
  episodeId: string;
  text: string;
  questionText: string;
  askedBy: string | null;
  questionType: 'explicit' | 'implicit';
  topic: string | null;
  context: string | null;
  contextText: string | null;
  answerSummary: string | null;
  timestampSeconds: number | null;
  timecode: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface QuestionBrowserItem extends SessionQuestion {
  sessionTitle: string;
  showSlug: string | null;
  showTitle: string | null;
  familySlug: string | null;
  familyTitle: string | null;
  familyVersionNumber: number | null;
  youtubeUrl: string | null;
  publishedToHomepage: boolean;
  sessionUpdatedAt: string | null;
  publishedAt: string | null;
  shareToken: string | null;
  publicUrl?: string | null;
}

export interface QuestionBrowserFacet {
  slug?: string;
  title?: string;
  name?: string;
  count: number;
}

export interface QuestionBrowserResponse {
  total: number;
  count: number;
  questions: QuestionBrowserItem[];
  shows: QuestionBrowserFacet[];
  people?: QuestionBrowserFacet[];
  topics?: QuestionBrowserFacet[];
  pagination?: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface QuestionListResponse {
  episodeId: string;
  total: number;
  count: number;
  questions: SessionQuestion[];
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface SessionQuestionInput {
  text?: string;
  questionText?: string;
  askedBy?: string | null;
  questionType?: 'explicit' | 'implicit' | null;
  topic?: string | null;
  context?: string | null;
  contextText?: string | null;
  answerSummary?: string | null;
  timestampSeconds?: number | null;
  sortOrder?: number | null;
}

type RawSessionQuestion = {
  id: string;
  episodeId?: string;
  session_id?: string;
  questionText?: string;
  question_text?: string;
  text?: string;
  askedBy?: string | null;
  asked_by?: string | null;
  questionType?: 'explicit' | 'implicit' | null;
  question_type?: 'explicit' | 'implicit' | null;
  topic?: string | null;
  context?: string | null;
  contextText?: string | null;
  context_text?: string | null;
  notes?: string | null;
  answerSummary?: string | null;
  answer_summary?: string | null;
  category?: string | null;
  source?: string | null;
  timestampSeconds?: number | string | null;
  timestamp_seconds?: number | string | null;
  timecode?: string | null;
  sortOrder?: number | string | null;
  sort_order?: number | string | null;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  sessionTitle?: string;
  session_title?: string;
  showSlug?: string | null;
  show_slug?: string | null;
  showTitle?: string | null;
  show_title?: string | null;
  familySlug?: string | null;
  family_slug?: string | null;
  familyTitle?: string | null;
  family_title?: string | null;
  familyVersionNumber?: number | string | null;
  family_version_number?: number | string | null;
  youtubeUrl?: string | null;
  youtube_url?: string | null;
  publishedToHomepage?: boolean | number | null;
  published_to_homepage?: boolean | number | null;
  sessionUpdatedAt?: string | null;
  session_updated_at?: string | null;
  publishedAt?: string | null;
  published_at?: string | null;
  shareToken?: string | null;
  share_token?: string | null;
  publicUrl?: string | null;
  public_url?: string | null;
};

function normalizeOptionalText(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeOptionalNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeQuestionTextInput(payload: Pick<SessionQuestionInput, 'text' | 'questionText'>): string {
  return String(payload.text ?? payload.questionText ?? '').trim();
}

function normalizeQuestionContextInput(payload: Pick<SessionQuestionInput, 'context' | 'contextText'>): string | null {
  return normalizeOptionalText(payload.context ?? payload.contextText);
}

function normalizeQuestion(raw: RawSessionQuestion): SessionQuestion {
  const text = String(raw.text ?? raw.questionText ?? raw.question_text ?? '').trim();
  const context = normalizeOptionalText(raw.context ?? raw.contextText ?? raw.context_text ?? raw.notes);

  return {
    id: raw.id,
    episodeId: raw.episodeId || raw.session_id || '',
    text,
    questionText: text,
    askedBy: normalizeOptionalText(raw.askedBy ?? raw.asked_by ?? raw.source),
    questionType: normalizeOptionalText(raw.questionType ?? raw.question_type) === 'implicit' ? 'implicit' : 'explicit',
    topic: normalizeOptionalText(raw.topic ?? raw.category),
    context,
    contextText: context,
    answerSummary: normalizeOptionalText(raw.answerSummary ?? raw.answer_summary),
    timestampSeconds: normalizeOptionalNumber(raw.timestampSeconds ?? raw.timestamp_seconds),
    timecode: normalizeOptionalText(raw.timecode),
    sortOrder: normalizeOptionalNumber(raw.sortOrder ?? raw.sort_order) ?? 0,
    createdAt: raw.createdAt || raw.created_at || '',
    updatedAt: raw.updatedAt || raw.updated_at || raw.createdAt || raw.created_at || '',
  };
}

function normalizeQuestionBrowserItem(raw: RawSessionQuestion): QuestionBrowserItem {
  const base = normalizeQuestion(raw);

  return {
    ...base,
    sessionTitle: String(raw.sessionTitle || raw.session_title || '').trim() || base.episodeId,
    showSlug: normalizeOptionalText(raw.showSlug ?? raw.show_slug),
    showTitle: normalizeOptionalText(raw.showTitle ?? raw.show_title),
    familySlug: normalizeOptionalText(raw.familySlug ?? raw.family_slug),
    familyTitle: normalizeOptionalText(raw.familyTitle ?? raw.family_title),
    familyVersionNumber: normalizeOptionalNumber(raw.familyVersionNumber ?? raw.family_version_number),
    youtubeUrl: normalizeOptionalText(raw.youtubeUrl ?? raw.youtube_url),
    publishedToHomepage: Boolean(raw.publishedToHomepage ?? raw.published_to_homepage),
    sessionUpdatedAt: normalizeOptionalText(raw.sessionUpdatedAt ?? raw.session_updated_at),
    publishedAt: normalizeOptionalText(raw.publishedAt ?? raw.published_at),
    shareToken: normalizeOptionalText(raw.shareToken ?? raw.share_token),
    publicUrl: normalizeOptionalText(raw.publicUrl ?? raw.public_url),
  };
}

function normalizeFacet(raw: any): QuestionBrowserFacet | null {
  const slug = normalizeOptionalText(raw?.slug);
  const title = normalizeOptionalText(raw?.title);
  const name = normalizeOptionalText(raw?.name);
  const count = Number(raw?.count || 0);

  if (!slug && !name) return null;

  return {
    slug: slug || undefined,
    title: title || undefined,
    name: name || undefined,
    count,
  };
}

function buildQueryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function toApiPayload(payload: SessionQuestionInput): Record<string, unknown> {
  const text = normalizeQuestionTextInput(payload);
  return {
    text,
    askedBy: normalizeOptionalText(payload.askedBy),
    questionType: normalizeOptionalText(payload.questionType) || 'explicit',
    topic: normalizeOptionalText(payload.topic),
    context: normalizeQuestionContextInput(payload),
    answerSummary: normalizeOptionalText(payload.answerSummary),
    timestampSeconds: normalizeOptionalNumber(payload.timestampSeconds),
    sortOrder: normalizeOptionalNumber(payload.sortOrder),
  };
}

async function fetchWithAuth(url: string, token?: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {})
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error || body.message || `Request failed: ${response.status}`);
  }
  return response;
}

export async function listQuestions(episodeId: string, token?: string): Promise<QuestionListResponse> {
  const response = await fetchWithAuth(`${API_BASE_URL}/episodes/${episodeId}/questions`, token);
  const body = await response.json();
  return {
    ...body,
    questions: Array.isArray(body.questions) ? body.questions.map(normalizeQuestion) : [],
  };
}

const PUBLIC_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

export async function listPublicShareQuestions(shareToken: string): Promise<QuestionListResponse> {
  const response = await fetch(`${PUBLIC_API_BASE_URL}/public/share/${shareToken}/questions`, {
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error || body.message || `Request failed: ${response.status}`);
  }
  const body = await response.json();
  return {
    ...body,
    questions: Array.isArray(body.questions) ? body.questions.map(normalizeQuestion) : [],
    pagination: body.pagination || { limit: body.total || 0, offset: 0, hasMore: false },
  };
}

export async function createQuestions(
  episodeId: string,
  payload: SessionQuestionInput | { questions: SessionQuestionInput[] },
  token?: string
): Promise<{ episodeId: string; count: number; questions: SessionQuestion[] }> {
  const requestBody = Array.isArray((payload as { questions?: SessionQuestionInput[] }).questions)
    ? {
        questions: (payload as { questions: SessionQuestionInput[] }).questions.map(toApiPayload),
      }
    : toApiPayload(payload as SessionQuestionInput);

  const response = await fetchWithAuth(`${API_BASE_URL}/episodes/${episodeId}/questions`, token, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
  const body = await response.json();
  return {
    ...body,
    questions: Array.isArray(body.questions) ? body.questions.map(normalizeQuestion) : [],
  };
}

export async function updateQuestion(
  episodeId: string,
  questionId: string,
  payload: Partial<SessionQuestionInput>,
  token?: string
): Promise<{ message: string; question: SessionQuestion }> {
  const requestBody: Record<string, unknown> = {};

  if (Object.prototype.hasOwnProperty.call(payload, 'text') || Object.prototype.hasOwnProperty.call(payload, 'questionText')) {
    requestBody.text = normalizeQuestionTextInput(payload);
  }
  if (payload.askedBy !== undefined) {
    requestBody.askedBy = normalizeOptionalText(payload.askedBy);
  }
  if (payload.questionType !== undefined) {
    requestBody.questionType = normalizeOptionalText(payload.questionType);
  }
  if (payload.topic !== undefined) {
    requestBody.topic = normalizeOptionalText(payload.topic);
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'context') || Object.prototype.hasOwnProperty.call(payload, 'contextText')) {
    requestBody.context = normalizeQuestionContextInput(payload);
  }
  if (payload.answerSummary !== undefined) {
    requestBody.answerSummary = normalizeOptionalText(payload.answerSummary);
  }
  if (payload.timestampSeconds !== undefined) {
    requestBody.timestampSeconds = normalizeOptionalNumber(payload.timestampSeconds);
  }
  if (payload.sortOrder !== undefined) {
    requestBody.sortOrder = normalizeOptionalNumber(payload.sortOrder);
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/episodes/${episodeId}/questions/${questionId}`, token, {
    method: 'PUT',
    body: JSON.stringify(requestBody),
  });
  const body = await response.json();
  return {
    ...body,
    question: normalizeQuestion(body.question),
  };
}

export async function deleteQuestion(
  episodeId: string,
  questionId: string,
  token?: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/episodes/${episodeId}/questions/${questionId}`, token, {
    method: 'DELETE',
  });
  return response.json();
}

export async function listQuestionBrowser(
  filters: {
    query?: string;
    show?: string;
    person?: string;
    topic?: string;
    discovery?: boolean;
    publishedOnly?: boolean;
    requireYoutube?: boolean;
    limit?: number;
    offset?: number;
  } = {},
  token?: string
): Promise<QuestionBrowserResponse> {
  const response = await fetchWithAuth(
    `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:7777'}/api/questions/browser${buildQueryString({
      q: filters.query,
      show: filters.show,
      person: filters.person,
      topic: filters.topic,
      discovery: filters.discovery ?? false,
      publishedOnly: filters.publishedOnly ?? true,
      requireYoutube: filters.requireYoutube ?? true,
      limit: filters.limit ?? 80,
      offset: filters.offset ?? 0,
    })}`,
    token
  );
  const body = await response.json();
  return {
    total: Number(body.total || 0),
    count: Number(body.count || 0),
    questions: Array.isArray(body.questions) ? body.questions.map(normalizeQuestionBrowserItem) : [],
    shows: Array.isArray(body.shows)
      ? body.shows
          .map(normalizeFacet)
          .filter((show: QuestionBrowserFacet | null): show is QuestionBrowserFacet => Boolean(show))
      : [],
    people: Array.isArray(body.people)
      ? body.people
          .map(normalizeFacet)
          .filter((person: QuestionBrowserFacet | null): person is QuestionBrowserFacet => Boolean(person))
      : [],
    topics: Array.isArray(body.topics)
      ? body.topics
          .map(normalizeFacet)
          .filter((topic: QuestionBrowserFacet | null): topic is QuestionBrowserFacet => Boolean(topic))
      : [],
    pagination: body.pagination
      ? {
          limit: Number(body.pagination.limit || 0),
          offset: Number(body.pagination.offset || 0),
          hasMore: Boolean(body.pagination.hasMore),
        }
      : undefined,
  };
}

export async function listPublicQuestionBrowser(
  filters: {
    query?: string;
    person?: string;
    topic?: string;
    discovery?: boolean;
    limit?: number;
    offset?: number;
  } = {}
): Promise<QuestionBrowserResponse> {
  const response = await fetch(
    `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:7777'}/api/public/homepage-questions${buildQueryString({
      q: filters.query,
      person: filters.person,
      topic: filters.topic,
      discovery: filters.discovery ?? false,
      limit: filters.limit ?? 40,
      offset: filters.offset ?? 0,
    })}`,
    {
      cache: 'no-store',
      credentials: 'omit',
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error || body.message || `Request failed: ${response.status}`);
  }
  const body = await response.json();
  return {
    total: Number(body.total || 0),
    count: Number(body.count || 0),
    questions: Array.isArray(body.questions) ? body.questions.map(normalizeQuestionBrowserItem) : [],
    shows: Array.isArray(body.shows)
      ? body.shows
          .map(normalizeFacet)
          .filter((show: QuestionBrowserFacet | null): show is QuestionBrowserFacet => Boolean(show))
      : [],
    people: Array.isArray(body.people)
      ? body.people
          .map(normalizeFacet)
          .filter((person: QuestionBrowserFacet | null): person is QuestionBrowserFacet => Boolean(person))
      : [],
    topics: Array.isArray(body.topics)
      ? body.topics
          .map(normalizeFacet)
          .filter((topic: QuestionBrowserFacet | null): topic is QuestionBrowserFacet => Boolean(topic))
      : [],
    pagination: body.pagination
      ? {
          limit: Number(body.pagination.limit || 0),
          offset: Number(body.pagination.offset || 0),
          hasMore: Boolean(body.pagination.hasMore),
        }
      : undefined,
  };
}
